import { z } from 'zod';

export const domainStatusSchema = z.enum(['Pending', 'DnsPending', 'Provisioning', 'Active', 'Error']);
const recordCheckSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('Matched') }).strict(),
  z.object({ status: z.literal('Mismatched'), observed: z.array(z.string()) }).strict(),
  z.object({ status: z.literal('LookupFailed') }).strict(),
]);
const addressCheckSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('Matched'), conflictingAAAA: z.array(z.string()) }).strict(),
  z
    .object({
      status: z.literal('Mismatched'),
      observed: z.array(z.string()),
      conflictingAAAA: z.array(z.string()),
    })
    .strict(),
  z.object({ status: z.literal('LookupFailed') }).strict(),
]);
export const domainRecordsSchema = z
  .object({
    address: z
      .object({
        type: z.literal('A'),
        name: z.string(),
        value: z.string(),
        required: z.literal(true),
        check: addressCheckSchema.nullable(),
      })
      .strict()
      .nullable(),
    ownership: z
      .object({
        type: z.literal('TXT'),
        name: z.string(),
        value: z.string(),
        required: z.literal(true),
        check: recordCheckSchema.nullable(),
      })
      .strict()
      .nullable(),
    discovery: z
      .object({
        type: z.literal('SVCB'),
        name: z.string(),
        value: z.string(),
        required: z.literal(false),
        check: recordCheckSchema.nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type DomainRecords = z.infer<typeof domainRecordsSchema>;
export type DnsRecord = NonNullable<DomainRecords[keyof DomainRecords]>;

export const domainDetailsSchema = z
  .object({
    records: domainRecordsSchema,
    certificateState: z.enum(['pending', 'provisioning', 'active', 'failed', 'local']),
    error: z.string().optional(),
  })
  .strict();
export type DomainDetails = z.infer<typeof domainDetailsSchema>;
export const domainDetailsReadSchema = domainDetailsSchema.extend({
  records: domainDetailsSchema.shape.records.default({ address: null, ownership: null, discovery: null }),
  certificateState: domainDetailsSchema.shape.certificateState.default('pending'),
});
export const customDomainSummarySchema = z.object({
  domain: z.string(),
  included: z.literal(false),
  status: domainStatusSchema,
  dnsAidPresent: z.boolean(),
  checkedAt: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  details: domainDetailsSchema,
});
export type CustomDomainSummary = z.infer<typeof customDomainSummarySchema>;
export const domainSummarySchema = z.discriminatedUnion('included', [
  z.object({
    domain: z.string(),
    included: z.literal(true),
    status: z.literal('Active'),
    dnsAidPresent: z.literal(false),
  }),
  customDomainSummarySchema,
]);
export type DomainSummary = z.infer<typeof domainSummarySchema>;
export const siteDomainsSchema = z.object({
  current: domainSummarySchema,
  pending: customDomainSummarySchema.nullable(),
});
export type SiteDomains = z.infer<typeof siteDomainsSchema>;
export const domainInputSchema = z
  .string()
  .trim()
  .max(253)
  .describe('Domain name, including a port when needed.');

export function requiredDomainRecords(hostname: string, ip: string, ownershipValue?: string): DomainRecords {
  return {
    address: { type: 'A', name: hostname, value: ip, required: true, check: null },
    ownership:
      ownershipValue !== undefined
        ? {
            type: 'TXT',
            name: `wirechunk.${hostname}`,
            value: ownershipValue,
            required: true,
            check: null,
          }
        : null,
    discovery: {
      type: 'SVCB',
      name: `_index._agents.${hostname}`,
      value: `1 ${hostname}. alpn="h2" port=443`,
      required: false,
      check: null,
    },
  };
}
